import React from 'react';
import { mockServices } from '../mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Droplet, Wrench, Hammer, Ruler, Building2, Construction, Scissors, Circle, ArrowRight, Paintbrush } from 'lucide-react';

const iconMap = {
  Droplet,
  Wrench,
  Hammer,
  Ruler,
  Building2,
  Construction,
  Scissors,
  Circle,
  Paintbrush
};

const Services = () => {
  const featuredService = mockServices.find(s => s.featured);
  const otherServices = mockServices.filter(s => !s.featured);

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <span className="px-6 py-2 bg-black text-white rounded-full text-sm font-semibold tracking-wider uppercase inline-block mb-4">
            What We Offer
          </span>
          <h2 className="text-5xl md:text-6xl font-extrabold text-black mb-6 tracking-tight">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light">
            Comprehensive construction and repair solutions tailored to your needs
          </p>
        </div>

        {/* Featured Waterproofing Service */}
        {featuredService && (
          <div className="mb-16">
            <Card className="group hover:shadow-3xl transition-all duration-500 border-0 overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 to-black">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-80 md:h-auto overflow-hidden">
                  <img
                    src={featuredService.image}
                    alt={featuredService.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 bg-white p-4 rounded-2xl shadow-2xl">
                    <Droplet className="w-10 h-10 text-black" />
                  </div>
                  <div className="absolute top-6 right-6 bg-white text-black px-5 py-2 rounded-full text-xs font-bold tracking-wider shadow-xl">
                    ★ FEATURED SERVICE
                  </div>
                </div>
                <div className="p-10 md:p-14 flex flex-col justify-center text-white">
                  <CardTitle className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight">
                    {featuredService.name}
                  </CardTitle>
                  <CardDescription className="text-gray-300 text-lg mb-8 leading-relaxed font-light">
                    {featuredService.description}
                  </CardDescription>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center space-x-4 group/item">
                      <div className="w-2 h-2 bg-white rounded-full group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-gray-200 font-light">Bathrooms & Kitchens</span>
                    </div>
                    <div className="flex items-center space-x-4 group/item">
                      <div className="w-2 h-2 bg-white rounded-full group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-gray-200 font-light">Terraces & Balconies</span>
                    </div>
                    <div className="flex items-center space-x-4 group/item">
                      <div className="w-2 h-2 bg-white rounded-full group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-gray-200 font-light">Underground & Overhead Tanks</span>
                    </div>
                    <div className="flex items-center space-x-4 group/item">
                      <div className="w-2 h-2 bg-white rounded-full group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-gray-200 font-light">Basement Waterproofing</span>
                    </div>
                  </div>
                  <button 
                    onClick={() => document.getElementById('quote').scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center space-x-2 text-white font-semibold group/btn"
                  >
                    <span>Get a Quote</span>
                    <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-2 transition-transform" />
                  </button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Other Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {otherServices.map((service) => {
            const Icon = iconMap[service.icon] || Wrench;
            return (
              <Card
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-500 border-0 overflow-hidden rounded-2xl hover:-translate-y-2 bg-white"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 bg-white p-3 rounded-xl shadow-xl">
                    <Icon className="w-6 h-6 text-black" />
                  </div>
                </div>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-bold text-black group-hover:text-gray-700 transition-colors leading-snug">
                    {service.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm font-light leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;